"use client";
/* eslint-disable @next/next/no-img-element -- brand logo is a supplied SVG used directly (ported from the v6 design). */

/**
 * Site header — rebuilt to the v6 editorial "site-header": one sticky near-black
 * bar (logo · thin nav · divided agent login · red Get-contracted CTA) with a
 * mobile sheet. Replaces the old two-tier (red promo strip + nav) header.
 * Shared by the App Router (layout.tsx) and Pages Router (_app.js). Styles in
 * src/styles/chrome.css. windowSizeHook.ts is kept — other components import it.
 */
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import "../../styles/chrome.css";

const PORTAL = "https://portal.agentboost.com";

/*
 * The header Login button (desktop CTA + mobile-sheet login) is always in the
 * DOM but its visibility is driven by the CSS variable --header-login-display
 * (see chrome.css), which defaults to `none` — a single-CTA header that pushes
 * agents toward Get Contracted instead of a competing Login. The Tweak Lab
 * flips that variable live (Header > Login button), and the footer login is
 * unaffected. To ship it on by default, change the default in chrome.css.
 */

const LINKS: ReadonlyArray<readonly [string, string]> = [
    ["Products", "/products"],
    ["Agent Tools", "/agent-center"],
    ["Training & Events", "/events"],
    ["About", "/about-us"],
    ["Podcast", "/podcast"],
    ["Blog", "/blog"],
    ["Contact", "/contact-us"],
];

const ArrowUpRight = ({ size = 17 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7" /><path d="M7 7h10v10" /></svg>
);
const MenuIcon = () => (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
);

export default function Header() {
    const path = usePathname();
    const [open, setOpen] = useState(false);

    // Close the mobile sheet on navigation.
    useEffect(() => {
        setOpen(false);
    }, [path]);

    return (
        <>
            <a className="skip-link" href="#main">
                Skip to content
            </a>
            <header className="site-header">
                <Link className="brand" href="/" aria-label="Agent Boost home">
                    <img src="/assets/agentboost-logo.svg" alt="Agent Boost Marketing" />
                </Link>
                <nav aria-label="Main navigation">
                    {LINKS.map(([label, href]) => (
                        <Link
                            key={href}
                            href={href}
                            aria-current={path === href ? "page" : undefined}
                        >
                            {label}
                        </Link>
                    ))}
                </nav>
                <a
                    className="header-cta header-login"
                    href={PORTAL}
                    target="_blank"
                    rel="noreferrer"
                >
                    Login <ArrowUpRight />
                </a>
                <Link className="header-cta" href="/get-contracted">
                    Get contracted <ArrowUpRight />
                </Link>
                <button
                    type="button"
                    className="mobile-menu"
                    aria-label="Open navigation"
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                >
                    <MenuIcon />
                </button>
            </header>

            {open && (
                <div className="mobile-sheet" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="sheet-close"
                        onClick={() => setOpen(false)}
                        aria-label="Close navigation"
                    >
                        CLOSE ✕
                    </button>
                    <div className="sheet-title">YOUR NEXT MOVE.</div>
                    <div className="sheet-sub">Built by agents. For agents.</div>
                    <nav aria-label="Mobile navigation">
                        {[
                            ...LINKS,
                            ["Get Contracted", "/get-contracted"] as const,
                            ["Careers", "/career"] as const,
                        ].map(([label, href], i) => (
                            <Link
                                key={href}
                                href={href}
                                onClick={() => setOpen(false)}
                            >
                                <span className="mono">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                {label}
                                <ArrowUpRight />
                            </Link>
                        ))}
                    </nav>
                    <a
                        className="sheet-login"
                        href={PORTAL}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Agent login <ArrowUpRight />
                    </a>
                </div>
            )}
        </>
    );
}
